import { useCallback } from 'react'
import { BlueprintAfterExecution, FlatInteractionModelData } from 'types/shared'
import { useAppDispatch } from 'state/store'
import { interactionModelOperations } from '../redux'
import { BlueprintTrigger } from './useBlueprintsContext'

export const useBlueprintsAfterExecution = (
  interactionModelData?: FlatInteractionModelData
) => {
  const dispatch = useAppDispatch()

  const activateActivityModels = useCallback(
    (
      changedActivityModel: string | undefined,
      sourceActivityModels: string[],
      targetActivityModels: string[]
    ) => {
      if (
        !changedActivityModel ||
        !sourceActivityModels.includes(changedActivityModel)
      ) {
        return
      }

      dispatch(
        interactionModelOperations.activateActivityModels(targetActivityModels)
      )
    },
    [dispatch]
  )

  const disableActivityModels = useCallback(
    (
      changedActivityModel: string | undefined,
      sourceActivityModels: string[],
      targetActivityModels: string[]
    ) => {
      if (
        !changedActivityModel ||
        !sourceActivityModels.includes(changedActivityModel)
      ) {
        return
      }

      dispatch(
        interactionModelOperations.disableAndClearActivityModels(
          targetActivityModels
        )
      )
    },
    [dispatch]
  )

  const executeAction = useCallback(
    (afterExecution: BlueprintAfterExecution, trigger?: BlueprintTrigger) => {
      if (afterExecution.action === 'activate_activitymodels') {
        if (trigger?.activityValue) {
          activateActivityModels(
            trigger?.activityModelUuid,
            afterExecution.source_activitymodels,
            afterExecution.target_activitymodels
          )
        } else {
          disableActivityModels(
            trigger?.activityModelUuid,
            afterExecution.source_activitymodels,
            afterExecution.target_activitymodels
          )
        }
      }
    },
    [activateActivityModels, disableActivityModels]
  )

  const executeActionsAfterBlueprints = useCallback(
    (trigger?: BlueprintTrigger) => {
      const internalMethodBlueprints = interactionModelData?.blueprints?.filter(
        (blueprint) => blueprint.internalmethod
      )

      if (!internalMethodBlueprints || internalMethodBlueprints.length === 0) {
        return
      }

      internalMethodBlueprints.forEach((blueprint) => {
        if (blueprint.after_execution?.length) {
          blueprint.after_execution.forEach((afterExecution) => {
            executeAction(afterExecution, trigger)
          })
        }
      })
    },
    [interactionModelData?.blueprints, executeAction]
  )

  return {
    executeActionsAfterBlueprints
  }
}
